import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CancelButton } from 'src/components/Buttons';
import { OkButton } from 'src/components/Buttons/OkButton';
import { Content, useContent } from 'src/contexts/ContentContext';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Stack } from 'src/core/ui';
import { useReachProCreateConversation } from 'src/hooks/api/useReachProCreateConversation';
import { ContentId } from 'src/utils/constants/contentId';

import { Modal, ModalBody, ModalFooter, ModalHeader } from '../Modal';
import * as styles from './CreateReachProConversationModal.css';

export type CreateReachProConversationModalProps = {
  isOpen?: boolean;
  onClose?: () => void;
  initialTopic?: string;
  initialEventId?: number;
  initialListingId?: number;
  initialSaleId?: number;
  initialSection?: string;
  initialRow?: string;
  initialSeatFrom?: string;
  initialSeatTo?: string;
};

export function CreateReachProConversationModal({
  isOpen,
  onClose,
  initialTopic = '',
  initialEventId,
  initialListingId,
  initialSaleId,
  initialSection,
  initialRow,
  initialSeatFrom,
  initialSeatTo,
}: CreateReachProConversationModalProps) {
  const navigate = useNavigate();

  const [topic, setTopic] = useState(initialTopic);
  const [eventId, setEventId] = useState(initialEventId?.toString() ?? '');
  const [listingId, setListingId] = useState(
    initialListingId?.toString() ?? ''
  );
  const [saleId, setSaleId] = useState(initialSaleId?.toString() ?? '');
  const [section, setSection] = useState(initialSection ?? '');
  const [row, setRow] = useState(initialRow ?? '');
  const [seatFrom, setSeatFrom] = useState(initialSeatFrom ?? '');
  const [seatTo, setSeatTo] = useState(initialSeatTo ?? '');

  // Sync state with props when modal opens
  useEffect(() => {
    if (isOpen) {
      setTopic(initialTopic);
      setEventId(initialEventId?.toString() ?? '');
      setListingId(initialListingId?.toString() ?? '');
      setSaleId(initialSaleId?.toString() ?? '');
      setSection(initialSection ?? '');
      setRow(initialRow ?? '');
      setSeatFrom(initialSeatFrom ?? '');
      setSeatTo(initialSeatTo ?? '');
    }
  }, [
    isOpen,
    initialTopic,
    initialEventId,
    initialListingId,
    initialSaleId,
    initialSection,
    initialRow,
    initialSeatFrom,
    initialSeatTo,
  ]);

  const createConversation = useReachProCreateConversation();

  const isValid =
    topic.trim() !== '' && eventId.trim() !== '' && !isNaN(parseInt(eventId));

  const handleCreate = async () => {
    if (!isValid) return;

    try {
      const conversation = await createConversation.mutateAsync({
        topic: topic.trim(),
        eventId: parseInt(eventId),
        listingId: listingId ? parseInt(listingId) : undefined,
        saleId: saleId ? parseInt(saleId) : undefined,
        section: section || undefined,
        row: row || undefined,
        seatFrom: seatFrom || undefined,
        seatTo: seatTo || undefined,
      });

      onClose?.();

      if (conversation) {
        navigate(`/reachpro-support/${conversation.conversationId}`);
      }

      setTopic('');
      setEventId('');
      setListingId('');
      setSaleId('');
      setSection('');
      setRow('');
      setSeatFrom('');
      setSeatTo('');
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
  };

  const handleClose = () => {
    setTopic('');
    setEventId('');
    setListingId('');
    setSaleId('');
    setSection('');
    setRow('');
    setSeatFrom('');
    setSeatTo('');
    onClose?.();
  };

  const issueWithTicketDeliveryPlaceholder = useContent(
    ContentId.IssueWithTicketDelivery
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      unmountOnClose
      centered
      size="sm"
    >
      <ModalHeader onClose={handleClose}>
        <div className={styles.headerText}>
          <Content id={ContentId.CreateSupportConversation} />
        </div>
      </ModalHeader>
      <ModalBody>
        <div className={styles.modalBody}>
          <Stack direction="column" gap="l">
            <div className={styles.fieldLabel}>
              <Content id={ContentId.Subject} />
              <span className={styles.required}>*</span>
            </div>
            <PosTextField
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={issueWithTicketDeliveryPlaceholder}
              maxLength={500}
            />

            <div className={styles.fieldLabel}>
              <Content id={ContentId.StubHubEventId} />
              <span className={styles.required}>*</span>
            </div>
            <PosTextField
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              type="number"
            />

            <div className={styles.fieldLabel}>
              <Content id={ContentId.ListingId} />
            </div>
            <PosTextField
              value={listingId}
              onChange={(e) => setListingId(e.target.value)}
              type="number"
            />

            <div className={styles.fieldLabel}>
              <Content id={ContentId.SaleId} />
            </div>
            <PosTextField
              value={saleId}
              onChange={(e) => setSaleId(e.target.value)}
              type="number"
            />

            <div className={styles.fieldLabel}>
              <Content id={ContentId.Section} />
            </div>
            <PosTextField
              value={section}
              onChange={(e) => setSection(e.target.value)}
              maxLength={200}
            />

            <div className={styles.fieldLabel}>
              <Content id={ContentId.Row} />
            </div>
            <PosTextField
              value={row}
              onChange={(e) => setRow(e.target.value)}
              maxLength={30}
            />

            <div className={styles.fieldLabel}>
              <Content id={ContentId.Seats} />
            </div>
            <Stack direction="row" gap="m">
              <PosTextField
                value={seatFrom}
                onChange={(e) => setSeatFrom(e.target.value)}
                placeholder="From"
                maxLength={10}
              />
              <PosTextField
                value={seatTo}
                onChange={(e) => setSeatTo(e.target.value)}
                placeholder="To"
                maxLength={10}
              />
            </Stack>
          </Stack>
        </div>
      </ModalBody>
      <ModalFooter>
        <CancelButton
          variant="regular"
          textContentId={ContentId.Cancel}
          onClick={handleClose}
        />
        <OkButton
          variant="regular"
          textContentId={ContentId.Create}
          onClick={handleCreate}
          disabled={!isValid || createConversation.isPending}
        />
      </ModalFooter>
    </Modal>
  );
}
